/**
 * LocationPicker — searchable warehouse bin/location picker.
 *
 * Wraps `EntityPickerBase` over `useLocations(warehouseId)`. Replaces the raw
 * `<Select>` location combos, which listed every bin of the warehouse as a flat,
 * unsearchable option list: a real WMS layout is a Cartesian product of
 * dimensions (`docs/modules/inventory/warehouse-locations-design.md` — a
 * `[Floor 1-4] x [North/South] x [Shelf A-Z]` warehouse is 220 nodes, and a
 * `2-S-M-01 … 2-S-M-25` slot run is another 25), so the flat list stopped being
 * usable long before a receiving clerk could scroll it.
 *
 * What the rows carry beyond the name, because it is what the operator actually
 * picks on: the location's cumulative position code (`F1-N-A` — significant, so
 * the code alone says where the bin physically is), its functional `purpose`
 * (staging / receiving / quarantine …) and a cold-chain marker when the bin
 * declares a temperature range. Search matches the name, the code, the purpose
 * label and every ancestor's name, so "cold", "staging" and "S-M-2" all find
 * their bins.
 *
 * Select-only by design: it offers no inline create. Bins are warehouse
 * infrastructure laid out ahead of time (there is a bulk generator for exactly
 * that), not something to invent halfway through booking a receipt.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, MapPin, Snowflake, TriangleAlert } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase } from '../common';
import { Badge } from '../../../components/ui/badge';
import { useLocations } from '../../inventory/hooks/useWarehouses';
import { filterAndSortBySearch } from '../../utils/searchScore';
import type { Location } from '../../../modules/inventory/types';

interface LocationPickerProps {
  /** Warehouse whose locations are offered. Empty = the picker has nothing to show. */
  warehouseId: string;
  /** Selected location id; null/'' = no bin chosen (loose stock). */
  value: string | null;
  /** Called with the location id, or null when cleared. */
  onChange: (locationId: string | null) => void;
  /** Optional label rendered above the trigger. */
  label?: string;
  /** Trigger placeholder when nothing is selected. */
  placeholder?: string;
  /**
   * Accessible name for the trigger where no visible label targets it — a
   * document line renders one picker per row, and `role="combobox"` takes no
   * name from its content.
   */
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  triggerId?: string;
  hasError?: boolean;
  required?: boolean;
  /** Locations already used elsewhere in the same line — hidden from the options. */
  excludeIds?: string[];
  /** Only offer bins stock can be picked from (outbound). */
  pickableOnly?: boolean;
}

/** True when the bin declares a temperature range — a cold room / chilled bay. */
function isTemperatureControlled(location: Location): boolean {
  return location.temperature_min_c !== null || location.temperature_max_c !== null;
}

export function LocationPicker({
  warehouseId,
  value,
  onChange,
  label,
  placeholder,
  ariaLabel,
  disabled,
  className,
  triggerId,
  hasError,
  required,
  excludeIds,
  pickableOnly = false,
}: LocationPickerProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Same query key as every other useLocations(warehouseId) consumer, so React
  // Query dedupes one fetch across all of a document's line pickers.
  const { data: allLocations } = useLocations(warehouseId);

  // parent_id -> name, so a bin can be found by typing an ancestor's name
  // ("north", "cold room") and the trigger can show where it sits.
  const nameById = useMemo(() => {
    const map = new Map<string, Location>();
    for (const loc of allLocations ?? []) map.set(loc.id, loc);
    return map;
  }, [allLocations]);

  const ancestorNames = useMemo(() => {
    const paths = new Map<string, string[]>();
    for (const loc of allLocations ?? []) {
      const names: string[] = [];
      let parentId = loc.parent_id;
      // Depth is bounded by the location hierarchy (building → bin, 7 levels);
      // the seen-set keeps a cyclic row from spinning here regardless.
      const seen = new Set<string>([loc.id]);
      while (parentId && !seen.has(parentId)) {
        seen.add(parentId);
        const parent = nameById.get(parentId);
        if (!parent) break;
        names.unshift(parent.name);
        parentId = parent.parent_id;
      }
      paths.set(loc.id, names);
    }
    return paths;
  }, [allLocations, nameById]);

  const excludeSet = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);

  const locations = useMemo(() => {
    const visible = (allLocations ?? []).filter(
      (loc) =>
        loc.is_active &&
        // The selected bin stays offered even if it is excluded elsewhere, so a
        // line never renders a picker whose own value is absent from its list.
        (loc.id === value || !excludeSet.has(loc.id)) &&
        (!pickableOnly || loc.is_pickable),
    );
    return filterAndSortBySearch(visible, search, (loc) =>
      [
        loc.name,
        loc.code,
        t(`inventory:locations.attributes.purposes.${loc.purpose}`),
        ...(ancestorNames.get(loc.id) ?? []),
      ]
        .filter(Boolean)
        .join(' '),
    );
  }, [allLocations, excludeSet, value, pickableOnly, search, ancestorNames, t]);

  return (
    <EntityPickerBase<Location>
      value={value ?? ''}
      items={locations}
      getItemLabel={(loc) => `${loc.name} (${loc.code})`}
      renderItem={(loc, isSelected) => (
        <>
          <Check className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
          <MapPin className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
          <span className="truncate">{loc.name}</span>
          <span className="ms-2 text-xs text-muted-foreground shrink-0">{loc.code}</span>
          {loc.purpose !== 'storage' && (
            <Badge variant="outline" className="ms-2 shrink-0 font-normal">
              {t(`inventory:locations.attributes.purposes.${loc.purpose}`)}
            </Badge>
          )}
          {isTemperatureControlled(loc) && (
            <Snowflake
              className="ms-1.5 h-3.5 w-3.5 shrink-0 text-info"
              aria-label={t('inventory:locations.attributes.temperatureRange')}
            />
          )}
          {loc.is_hazmat && (
            <TriangleAlert
              className="ms-1.5 h-3.5 w-3.5 shrink-0 text-warning"
              aria-label={t('inventory:locations.attributes.hazmat')}
            />
          )}
        </>
      )}
      label={label}
      placeholder={placeholder ?? t('inventory:locationPicker.searchLocations')}
      searchPlaceholder={t('inventory:locationPicker.searchLocations')}
      emptyMessage={t('inventory:locationPicker.noLocationsFound')}
      ariaLabel={ariaLabel}
      disabled={disabled}
      className={className}
      triggerId={triggerId}
      hasError={hasError}
      required={required}
      search={search}
      onSearchChange={setSearch}
      open={open}
      onOpenChange={setOpen}
      onSelect={(locationId) => {
        onChange(locationId);
        setOpen(false);
        setSearch('');
      }}
      allowClear
      clearLabel={t('inventory:locationPicker.noLocation')}
      onClear={() => {
        onChange(null);
        setOpen(false);
        setSearch('');
      }}
    />
  );
}
